import React, { useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { ScanBarcode, Camera, X, Check } from 'lucide-react';

export const Scanner = ({ onCode, onClose, scanId = 'imei-scanner', paused = false }) => {
  const scRef = useRef(null);
  const startedRef = useRef(false);
  const pausedRef = useRef(paused);
  pausedRef.current = paused;
  useEffect(() => {
    let cancelled = false;
    let last = '';
    import('html5-qrcode').then(({ Html5Qrcode }) => {
      if (cancelled || startedRef.current) return;
      startedRef.current = true;
      const el = document.getElementById(scanId);
      // replaceChildren() limpia hijos sin usar innerHTML (más seguro contra inyección de HTML)
      if (el) el.replaceChildren();
      const sc = new Html5Qrcode(scanId);
      scRef.current = sc;
      sc.start({ facingMode: 'environment' }, { fps: 10, qrbox: { width: 280, height: 110 } }, (txt) => {
        if (pausedRef.current) { last = ''; return; }
        if (txt !== last) { last = txt; onCode(txt); }
      }).catch(() => toast.error('SalesPRO: No se pudo abrir la cámara. Revisa los permisos.'));
    });
    return () => {
      cancelled = true;
      const sc = scRef.current;
      if (sc) { sc.stop().then(() => sc.clear()).catch(() => {}); scRef.current = null; }
      startedRef.current = false;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="space-y-2">
      <div id={scanId} className="w-full rounded-lg overflow-hidden bg-black min-h-[200px]" data-testid="imei-scanner" />
      <p className="text-[11px] text-slate-500">Apunta al código de barras o IMEI. También funciona con pistola lectora USB.</p>
      <Button variant="outline" size="sm" onClick={onClose}><X size={14} className="mr-1" /> Cerrar cámara</Button>
    </div>
  );
};

// Carga de IMEI por unidad (escáner con cámara o manual). Cada IMEI suma 1 al stock.
export const UnitsDialog = ({ product, onDone }) => {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [scan, setScan] = useState(false);
  const [pending, setPending] = useState(null);
  const [units, setUnits] = useState([]);
  const load = () => api.getUnits(product.product_id).then((r) => setUnits(r.data)).catch(() => {});
  useEffect(() => { if (open) load(); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const listOf = (t) => t.split(/[\n,;\s]+/).map((x) => x.trim()).filter(Boolean);
  const onCode = (c) => {
    if (pending) return;
    const code = c.replace(/\D/g, '').slice(0, 17) || c;
    setPending(code);
  };
  const confirmPending = () => {
    const code = pending;
    if (listOf(text).includes(code)) { toast.info(`IMEI ${code} ya estaba en la lista`); }
    else { setText((t) => `${t ? `${t}\n` : ''}${code}`); toast.success(`IMEI agregado: ${code}`); }
    setPending(null);
  };
  const discardPending = () => { toast.message('IMEI descartado, escanea de nuevo'); setPending(null); };
  const save = async () => {
    const imeis = listOf(text);
    if (!imeis.length) return;
    try { const r = await api.addUnits(product.product_id, imeis); toast.success(`SalesPRO: ${r.data.added} equipo(s) agregados al inventario`); setText(''); setScan(false); setPending(null); load(); onDone && onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || 'SalesPRO: Error'); }
  };
  const closeScan = () => { setScan(false); setPending(null); };
  const readyCount = listOf(text).length;
  return (
    <>
      <Button size="icon" variant="ghost" title="IMEI / unidades" onClick={() => setOpen(true)} data-testid={`units-btn-${product.product_id}`}><ScanBarcode size={14} /></Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="units-dialog">
          <DialogHeader><DialogTitle>IMEI · {product.name}</DialogTitle></DialogHeader>
          <p className="text-xs text-[#64748B]">Escanea el código de barras de cada caja (uno por uno, confirmando cada IMEI) o escribe un IMEI por línea. Cada IMEI es un equipo y suma 1 al stock.</p>
          {scan ? (
            <div className="space-y-2">
              <Scanner onCode={onCode} onClose={closeScan} paused={!!pending} />
              {pending ? (
                <div className="rounded-lg border-2 border-primary bg-primary/5 p-3 space-y-2" data-testid="imei-confirm-card">
                  <p className="text-[11px] text-slate-600">IMEI leído — confirma para agregarlo:</p>
                  <p className="font-mono text-lg font-bold text-primary break-all" data-testid="imei-confirm-code">{pending}</p>
                  <div className="flex gap-2">
                    <Button size="sm" className="flex-1" onClick={confirmPending} data-testid="imei-confirm-btn"><Check size={14} className="mr-1" /> Confirmar y agregar</Button>
                    <Button size="sm" variant="outline" className="flex-1" onClick={discardPending} data-testid="imei-discard-btn"><X size={14} className="mr-1" /> Descartar</Button>
                  </div>
                </div>
              ) : (
                <p className="text-[11px] font-medium text-amber-700" data-testid="scan-hint">Apunta al siguiente equipo para leer su IMEI…</p>
              )}
              <p className="text-[11px] text-green-700" data-testid="ready-count">{readyCount} equipo(s) listos para registrar.</p>
            </div>
          ) : <Button variant="outline" onClick={() => setScan(true)} data-testid="open-scanner-btn"><Camera size={14} className="mr-1" /> Escanear con la cámara</Button>}
          <Textarea rows={5} value={text} onChange={(e) => setText(e.target.value)} placeholder="356789012345671" className="font-mono text-xs" data-testid="imei-textarea" />
          <Button onClick={save} data-testid="save-imeis-btn">Registrar {readyCount || ''} equipo(s)</Button>
          <div className="max-h-48 overflow-y-auto text-xs">
            {units.map((u) => <div key={u.unit_id} className="flex justify-between border-b py-1 font-mono"><span>{u.imei}</span><span className={u.status === 'disponible' ? 'text-green-700' : 'text-amber-700'}>{u.status}</span></div>)}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

// Selector de IMEI al facturar (obligatorio para equipos): escáner con cámara o selección manual
export const ImeiPicker = ({ productId, value = [], qty = 1, onChange }) => {
  const [units, setUnits] = useState([]);
  const [scan, setScan] = useState(false);
  useEffect(() => { if (productId) api.getUnits(productId, 'disponible').then((r) => setUnits(r.data)).catch(() => setUnits([])); }, [productId]);
  if (!units.length) return null;
  const toggle = (imei) => onChange(value.includes(imei) ? value.filter((x) => x !== imei) : [...value, imei].slice(-Math.max(1, qty)));
  const onCode = (c) => {
    const code = (c.replace(/\D/g, '').slice(0, 17)) || c;
    const unit = units.find((u) => u.imei === code);
    if (!unit) { toast.error(`SalesPRO: El IMEI ${code} no está disponible en esta sucursal`); return; }
    if (!value.includes(code)) { onChange([...value, code].slice(-Math.max(1, qty))); toast.success(`IMEI a entregar: ${code}`); }
  };
  const complete = value.length >= qty;
  return (
    <div className="mt-1 w-full" data-testid={`imei-picker-${productId}`}>
      <div className="flex items-center gap-2 mb-1">
        <span className={`text-[10px] font-semibold ${complete ? 'text-green-700' : 'text-amber-700'}`} data-testid={`imei-status-${productId}`}>
          {complete ? `✓ ${qty} IMEI seleccionado(s)` : `Obligatorio: escanea/selecciona ${qty} IMEI (${value.length}/${qty})`}
        </span>
        <Button type="button" size="sm" variant="outline" className="h-6 text-[10px] px-2" onClick={() => setScan((s) => !s)} data-testid={`imei-scan-btn-${productId}`}>
          <Camera size={12} className="mr-1" /> {scan ? 'Cerrar' : 'Escanear equipo'}
        </Button>
      </div>
      {scan && <Scanner scanId={`imei-scan-${productId}`} onCode={onCode} onClose={() => setScan(false)} />}
      <div className="flex flex-wrap gap-1">
        {units.map((u) => <button type="button" key={u.imei} onClick={() => toggle(u.imei)} className={`text-[10px] font-mono px-1.5 py-0.5 rounded border ${value.includes(u.imei) ? 'bg-primary text-white border-primary' : 'border-[#CBD5E1] text-[#64748B]'}`}>{u.imei}</button>)}
      </div>
    </div>
  );
};

export default UnitsDialog;
